import { useCallback, useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from "react";

/**
 * Unobtrusive "⋮" menu. Submenus expand inline (no flyouts). Radio items show
 * a check and an optional muted description. Closes on click-outside, Esc,
 * or selecting an action/radio.
 */
export type MenuItem =
  | { kind: "action"; label: string; onSelect: () => void; disabled?: boolean }
  | { kind: "submenu"; label: string; valueLabel?: string; children: MenuItem[] }
  | { kind: "radio"; label: string; description?: string; checked: boolean; onSelect: () => void; disabled?: boolean };

/** Flattened, keyboard-navigable row. Pure so it can be tested. */
export interface MenuRow {
  key: string;
  item: MenuItem;
  depth: number;
}

export function flattenItems(items: MenuItem[], openKeys: Set<string>, prefix = "", depth = 0): MenuRow[] {
  const rows: MenuRow[] = [];
  items.forEach((item, i) => {
    const key = `${prefix}${i}`;
    rows.push({ key, item, depth });
    if (item.kind === "submenu" && openKeys.has(key)) {
      rows.push(...flattenItems(item.children, openKeys, `${key}.`, depth + 1));
    }
  });
  return rows;
}

/** Next enabled row index in a direction, wrapping. -1 when nothing is enabled. */
export function nextIndex(rows: MenuRow[], from: number, dir: 1 | -1): number {
  if (rows.length === 0) return -1;
  for (let step = 1; step <= rows.length; step++) {
    const i = (from + dir * step + rows.length * step) % rows.length;
    const it = rows[i]!.item;
    if (!("disabled" in it && it.disabled)) return i;
  }
  return -1;
}

export function KebabMenu({
  items,
  label = "More options",
  className,
}: {
  items: MenuItem[];
  label?: string;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [openKeys, setOpenKeys] = useState<Set<string>>(() => new Set());
  const [active, setActive] = useState(-1);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const menuId = useId();

  const rows = useMemo(() => flattenItems(items, openKeys), [items, openKeys]);

  const close = useCallback(() => {
    setOpen(false);
    setActive(-1);
  }, []);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) close();
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open, close]);

  function toggleSub(key: string) {
    setOpenKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function activate(row: MenuRow) {
    const it = row.item;
    if ("disabled" in it && it.disabled) return;
    if (it.kind === "submenu") {
      toggleSub(row.key);
      return;
    }
    it.onSelect();
    close();
    triggerRef.current?.focus();
  }

  function onKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (!open) return;
    if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      close();
      triggerRef.current?.focus();
      return;
    }
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => nextIndex(rows, a, e.key === "ArrowDown" ? 1 : -1));
      return;
    }
    if (e.key === "Enter" || e.key === " ") {
      if (active >= 0 && rows[active]) {
        e.preventDefault();
        activate(rows[active]!);
      }
      return;
    }
    if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
      const row = rows[active];
      if (row?.item.kind === "submenu") {
        e.preventDefault();
        const isOpen = openKeys.has(row.key);
        if ((e.key === "ArrowRight") !== isOpen) toggleSub(row.key);
      }
    }
  }

  return (
    <div className={["kebab", className ?? ""].join(" ")} ref={rootRef} onKeyDown={onKeyDown}>
      <button
        ref={triggerRef}
        type="button"
        className="btn btn-sm btn-icon kebab-trigger"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        title={label}
        onClick={() => (open ? close() : setOpen(true))}
        onKeyDown={(e) => {
          if (!open && (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            setOpen(true);
            setActive(nextIndex(rows, -1, 1));
          }
        }}
      >
        ⋮
      </button>
      {open && (
        <div className="kebab-menu" role="menu" id={menuId}>
          {rows.map((row, i) => {
            const it = row.item;
            const isActive = i === active;
            const disabled = "disabled" in it && !!it.disabled;
            const base = ["kebab-item", `depth-${row.depth}`, isActive ? "active" : "", disabled ? "disabled" : ""].join(" ");
            if (it.kind === "submenu") {
              const isOpen = openKeys.has(row.key);
              return (
                <button
                  key={row.key}
                  type="button"
                  role="menuitem"
                  className={`${base} kebab-submenu`}
                  aria-expanded={isOpen}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => activate(row)}
                >
                  <span className="kebab-label">{it.label}</span>
                  {it.valueLabel && <span className="kebab-value muted">{it.valueLabel}</span>}
                  <span className="kebab-chevron" aria-hidden>
                    {isOpen ? "▾" : "▸"}
                  </span>
                </button>
              );
            }
            if (it.kind === "radio") {
              return (
                <button
                  key={row.key}
                  type="button"
                  role="menuitemradio"
                  aria-checked={it.checked}
                  className={`${base} kebab-radio ${it.checked ? "checked" : ""}`}
                  disabled={disabled}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => activate(row)}
                >
                  <span className="kebab-check" aria-hidden>
                    {it.checked ? "✓" : ""}
                  </span>
                  <span className="kebab-radio-body">
                    <span className="kebab-label">{it.label}</span>
                    {it.description && <span className="kebab-desc muted small">{it.description}</span>}
                  </span>
                </button>
              );
            }
            return (
              <button
                key={row.key}
                type="button"
                role="menuitem"
                className={base}
                disabled={disabled}
                onMouseEnter={() => setActive(i)}
                onClick={() => activate(row)}
              >
                <span className="kebab-label">{it.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
